'use client';

import {
  type Dispatch,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import { getPermalinkEncodedFilterKeys } from '@/features/seo-listing/consts';
import type { SeoResolveData } from '@/features/seo-listing/types';
import {
  syncListingPaginationDocumentMeta,
  syncListingQueryRobotsDocumentMeta,
} from '@/features/seo-listing/utils';
import type { FilterAction, FilterState } from '../store';
import type { ListingRestoreSnapshot } from '../types';
import { buildFilterUpdatesFromUrl, getBrowserPathname, pushListingFiltersToUrl } from '../utils';

interface UseListingUrlSyncParams {
  filterState: FilterState;
  dispatch: Dispatch<FilterAction>;
  pathname: string;
  debouncedSearchQuery: string;
  isSeoPendingRef: RefObject<boolean>;
  isPopstateNavigationRef: RefObject<boolean>;
  seoPage: SeoResolveData | null;
  listingRestoreSnapshot: ListingRestoreSnapshot | null;
}

/**
 * Two-way sync between filter state and the address bar: pushes filters into
 * the URL, patches document meta, resets page on search, and reads the URL
 * back into filter state (mount / popstate).
 */
export const useListingUrlSync = ({
  filterState,
  dispatch,
  pathname,
  debouncedSearchQuery,
  isSeoPendingRef,
  isPopstateNavigationRef,
  seoPage,
  listingRestoreSnapshot,
}: UseListingUrlSyncParams) => {
  const updateURL = pushListingFiltersToUrl;

  const syncStateFromUrl = useCallback((): boolean => {
    const browserSearchParams = new URLSearchParams(window.location.search);
    const updates = buildFilterUpdatesFromUrl(
      browserSearchParams,
      {
        currentPage: filterState.currentPage,
        sortBy: filterState.sortBy,
        searchQuery: filterState.searchQuery,
        selectedFandoms: filterState.selectedFandoms,
        selectedTags: filterState.selectedTags,
        selectedUniverses: filterState.selectedUniverses,
        selectedStatuses: filterState.selectedStatuses,
        selectedRatings: filterState.selectedRatings,
        selectedCharacters: filterState.selectedCharacters,
        selectedPairingCharacters: filterState.selectedPairingCharacters,
      },
      getPermalinkEncodedFilterKeys(getBrowserPathname()),
    );

    if (updates) {
      dispatch({ type: 'SYNC_FROM_URL', payload: updates });
      return true;
    }
    return false;
  }, [
    dispatch,
    filterState.currentPage,
    filterState.searchQuery,
    filterState.selectedCharacters,
    filterState.selectedFandoms,
    filterState.selectedPairingCharacters,
    filterState.selectedRatings,
    filterState.selectedStatuses,
    filterState.selectedTags,
    filterState.selectedUniverses,
    filterState.sortBy,
  ]);

  // Soft Back remounts this grid after popstate already fired (listener was
  // unmounted on the work page). Without listing-restore snapshot, re-read
  // window.location so ?page= matches filter state before the first fetch skip.
  // Do NOT set isPopstateNavigation here: when URL already matches SSR filters,
  // sync is a no-op and a leftover flag would swallow the first status/rating/
  // pass-through-tag edit (grid updates, address bar stays stale until toggle).
  useLayoutEffect(() => {
    if (listingRestoreSnapshot) {
      return;
    }
    syncStateFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount
  }, []);

  // Sync the URL when filters change (skip the first render, popstate
  // navigation, and the window while an SEO redirect result is pending).
  const isFirstURLUpdateRef = useRef(true);
  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    if (isFirstURLUpdateRef.current) {
      isFirstURLUpdateRef.current = false;
      prevPathnameRef.current = pathname;
      // Soft-Back restore (and any prior mount sync) may leave this true while
      // the first effect run is skipped — clear so the next real filter edit
      // still writes statuses/ratings/pass-through tags into the address bar.
      isPopstateNavigationRef.current = false;
      return;
    }

    // Skip updateURL if pathname changed since the previous run of this effect,
    // regardless of the cause (browser back/forward via popstate, or a
    // programmatic pushState inside useSeoRedirect). In both cases the address
    // bar is owned by another mechanism at the moment pathname changes
    // (syncStateFromUrl on popstate, or pushState/replaceState inside
    // useSeoRedirect), and filterState in this render is not yet updated for the
    // new path — otherwise updateURL would write stale filter values over the
    // just-changed pathname and corrupt the history entry.
    //
    // Next.js updates usePathname() synchronously on popstate through its own
    // internal listener, which is registered before ours — so this effect fires
    // with the fresh pathname but the old filterState, BEFORE our
    // handlePopState/syncStateFromUrl handles the same popstate event.
    if (prevPathnameRef.current !== pathname) {
      prevPathnameRef.current = pathname;
      return;
    }

    // Skip updateURL when the state change came from popstate (back/forward button)
    if (isPopstateNavigationRef.current) {
      isPopstateNavigationRef.current = false;
      return;
    }

    // Skip updateURL while waiting for the /api/v1/seo/redirect result — the
    // address bar is temporarily controlled by useSeoRedirect in this window
    // (pushState to toUrl, or the fallback inside onSeoDisabled). The flag is
    // cleared in onNavigationSettled right after the synchronous (flushSync)
    // commit of the related state update, so a race with this effect is
    // structurally impossible.
    if (isSeoPendingRef.current) {
      return;
    }

    updateURL({
      page: filterState.currentPage,
      sort: filterState.sortBy,
      search: debouncedSearchQuery,
      fandoms: filterState.selectedFandoms,
      tags: filterState.selectedTags,
      universes: filterState.selectedUniverses,
      statuses: filterState.selectedStatuses,
      ratings: filterState.selectedRatings,
      characters: filterState.selectedCharacters,
      pairingCharacters: filterState.selectedPairingCharacters,
    });
  }, [
    filterState.currentPage,
    filterState.sortBy,
    debouncedSearchQuery,
    filterState.selectedFandoms,
    filterState.selectedTags,
    filterState.selectedUniverses,
    filterState.selectedStatuses,
    filterState.selectedRatings,
    filterState.selectedCharacters,
    filterState.selectedPairingCharacters,
    pathname,
    updateURL,
    isPopstateNavigationRef,
    isSeoPendingRef,
  ]);

  // Soft URL updates skip Next.js Metadata API — keep robots / title /
  // description / self-canonical in sync for search/sort/pagination.
  useEffect(() => {
    syncListingQueryRobotsDocumentMeta({
      search: debouncedSearchQuery,
      sort: filterState.sortBy,
      page: filterState.currentPage,
      fallbackRobots: seoPage?.robots,
    });
    syncListingPaginationDocumentMeta({
      page: filterState.currentPage,
      baseTitle: seoPage?.title,
      baseDescription: seoPage?.description,
    });
  }, [
    debouncedSearchQuery,
    filterState.sortBy,
    filterState.currentPage,
    seoPage?.robots,
    seoPage?.title,
    seoPage?.description,
  ]);

  // Always reset the page to 1 when search changes (skip the first render).
  // Tied to debouncedSearchQuery — otherwise every keystroke would dispatch
  // RESET_PAGE and needlessly re-run the URL/listing effects.
  const isFirstSearchResetRef = useRef(true);
  useEffect(() => {
    if (isFirstSearchResetRef.current) {
      isFirstSearchResetRef.current = false;
      return;
    }
    dispatch({ type: 'RESET_PAGE' });
  }, [debouncedSearchQuery, dispatch]);

  return { updateURL, syncStateFromUrl };
};
